import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import './index.css';
import './admin.css';

// --- ADMIN PAGES ---
import DashboardOverview from './pages/DashboardOverview';
import EstablishmentsManager from './pages/EstablishmentsManager';
import EstablishmentDetails from './pages/EstablishmentDetails';
import UsersManager from './pages/UsersManager';
import MenuCategoriesManager from './pages/MenuCategoriesManager';
import AdminAuth from './pages/AdminAuth';
import AdminSettings from './pages/AdminSettings';

// --- COMPONENTS ---
import { LandingApp } from './pages/LandingApp';
import { PlaceholderPage } from './pages/PlaceholderPage';
import { DashboardLayout } from './components/admin/DashboardLayout';
import { ProductPage } from './pages/ProductPage';
import { SolutionsPage } from './pages/SolutionsPage';
import { PricingPage } from './pages/PricingPage';
import { AboutPage } from './pages/AboutPage';
import { TermsPage } from './pages/TermsPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { ContactPage } from './pages/ContactPage';

export default function App() {
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  if (loading) {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#0a0a0c', color: 'white', gap: '20px' }}>
        <img src="/icon.svg" alt="Ordabell" style={{ height: '64px', width: '64px', animation: 'pulse 2s infinite ease-in-out' }} />
        <div style={{ fontSize: '1.25rem', fontWeight: 500, fontFamily: 'var(--font-display)' }}>Ordabell</div>
        <style>{`
          @keyframes pulse {
            0% { opacity: 0.6; transform: scale(0.95); }
            50% { opacity: 1; transform: scale(1); }
            100% { opacity: 0.6; transform: scale(0.95); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Public Landing Site Route */}
        <Route path="/" element={<LandingApp />} />

        {/* Secondary SaaS Pages */}
        <Route path="/product" element={<ProductPage />} />
        <Route path="/solutions" element={<SolutionsPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />

        {/* Under Construction Pages */}
        <Route path="/security" element={<PlaceholderPage title="Trust & Security" />} />
        <Route path="/blog" element={<PlaceholderPage title="Hospitality Insights Blog" />} />

        {/* Redirect old /admin URL to home to prevent URL enumeration */}
        <Route path="/admin" element={<Navigate to="/" />} />
        <Route path="/admin/*" element={<Navigate to="/" />} />

        {/* Admin Gateway — obfuscated path */}
        <Route path="/xm-console/login" element={
          !session ? (
            <div className="admin-dashboard">
              <AdminAuth />
            </div>
          ) : <Navigate to="/xm-console" />
        } />

        {/* Protected Dashboard Routes nested under /xm-console */}
        <Route path="/xm-console/*" element={
          session ? (
            <DashboardLayout onSignOut={handleSignOut}>
              <Routes>
                <Route path="/" element={<DashboardOverview />} />
                <Route path="/establishments" element={<EstablishmentsManager />} />
                <Route path="/establishments/:id" element={<EstablishmentDetails />} />
                <Route path="/users" element={<UsersManager />} />
                <Route path="/menu-categories" element={<MenuCategoriesManager />} />
                <Route path="/settings" element={<AdminSettings />} />
                <Route path="*" element={<Navigate to="/xm-console" />} />
              </Routes>
            </DashboardLayout>
          ) : (
            <Navigate to="/xm-console/login" />
          )
        } />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
  );
}
